import React from 'react';
import { i18n } from 'config/locales';
import { AliasModel } from 'forms/shared/types/alias';
import { ErrorCode } from '@redux/modules/application/reducers/error';

import { CommonOnUpdate, CommonOnError } from 'types/props';
import { DateModel } from 'forms/shared/types';
import ValidationElement, {
  ValidationElementProps,
} from 'components/Form/ValidationElement/ValidationElement';
import Field from 'components/Form/Field';
import Name from 'components/Form/Name';
import Show from 'components/Form/Show';
import Branch from 'components/Form/Branch';
import DateRange from 'components/Form/DateRange';
import Textarea from 'components/Form/Textarea';
import Comments from 'components/Form/Comments';
import { isPVQForm } from 'helpers/form';
import PVQName from 'components/Form/PVQName';
import NameOptions from 'components/Form/NameOptions';

interface Props extends Partial<AliasModel>, ValidationElementProps {
  hideMaiden: boolean
  minDate: DateModel
  required: boolean
  relationship: any
  onUpdate: CommonOnUpdate
  onError: CommonOnError
  scrollIntoView: boolean
  attribute: string
  formType?: string // pvq only
}

export default class Alias extends ValidationElement<Props> {
  handleNameError = (value: AliasModel['Name'], arr: ErrorCode[]) => {
    const arr2 = arr.map((err) => ({
      ...err,
      code:
        err.code.includes('required')
          ? `name${err.code.substring(err.code.lastIndexOf('.'))}`
          : err.code,
    }));
    return this.props.onError(value, arr2);
  };

  update = (queue: Partial<AliasModel>) => {
    this.props.onUpdate({
      Name: this.props.Name,
      MaidenName: this.props.MaidenName,
      Dates: this.props.Dates,
      Reason: this.props.Reason,
      Comments: this.props.Comments,
      NameOptions: this.props.NameOptions, // pvq only
      NameOptionsExplanation: this.props.NameOptionsExplanation, // pvq only
      ...queue,
    });
  };

  updateNameOptions = (values: AliasModel['NameOptions']) => {
    this.update({
      NameOptions: values,
    });
  };

  updateNameOptionsExplanation = (values: AliasModel['NameOptionsExplanation']) => {
    this.update({
      NameOptionsExplanation: values,
    });
  };

  updateName = (values: AliasModel['Name']) => {
    this.update({
      Name: values,
    });
  };

  updateMaidenName = (values: AliasModel['MaidenName']) => {
    this.update({
      MaidenName: values,
    });
  };

  updateDates = (values: AliasModel['Dates']) => {
    this.update({
      Dates: values,
    });
  };

  updateReason = (values: AliasModel['Reason']) => {
    this.update({
      Reason: values,
    });
  };

  updateComments = (values: AliasModel['Comments']) => {
    this.update({
      Comments: values,
    });
  };

  render = () => {
    const attribute = `${this.props.attribute}.items[${this.props.$index}].Item`;
    const isPvq = isPVQForm(this.props.formType);
    return isPvq ? (
      <div className="relative-alias">
        <Field
          title={i18n.t('relationships.pvq.relatives.heading.alias.title')}
          attribute={`${attribute}.Title`}
          optional
          filterErrors={Name.requiredErrorsOnly}
          scrollIntoView={this.props.scrollIntoView}
        >
          <PVQName
            name="Name"
            className="alias-name"
            attribute={`${attribute}.Name`}
            aria-label="Provide other names that your relative used"
            {...this.props.Name}
            onUpdate={this.updateName}
            onError={this.handleNameError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
          />
        </Field>
        <Field
          title={i18n.t('relationships.pvq.relatives.heading.alias.describeName')}
          help="alias.maiden.help"
          adjustFor="buttons"
          shrink
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.NameOptions`}
        >
          <NameOptions
            name="NameOptions"
            {...this.props.NameOptions}
            onUpdate={this.updateNameOptions}
            aria-label="Which of the following best describes this name?"
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.NameOptions`}
          />
        </Field>
        {this.props.NameOptions?.value === 'Other' && (
          <div>
            <Field
              title={i18n.t('relationships.pvq.relatives.heading.alias.other')}
              shrink
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.NameOptionsExplanation`}
            >
              <Textarea
                attribute={`${attribute}.NameOptionsExplanation`}
                name="NameOptionsExplanation"
                {...this.props.NameOptionsExplanation}
                className="name-options-explanation"
                onUpdate={this.updateNameOptionsExplanation}
                onError={this.props.onError}
                required={this.props.required}
              />
            </Field>
          </div>
        )}
        <Field
          title={i18n.t('relationships.pvq.relatives.heading.alias.whenUsed')}
          attribute={`${attribute}.Dates`}
          optional
          scrollIntoView={this.props.scrollIntoView}
        >
          <DateRange
            name="Dates"
            attribute={`${attribute}.Dates`}
            className="alias-dates"
            {...this.props.Dates}
            prefix="relative"
            minDateEqualTo
            minDate={this.props.minDate}
            relationship={this.props.relationship}
            onUpdate={this.updateDates}
            onError={this.props.onError}
            required={this.props.required}
            hideFromDay
            hideToDay
          />
        </Field>
      </div>
    ) : (
      <div className="relative-alias">
        <Field
          title={i18n.t('relationships.relatives.heading.alias.title')}
          attribute={`${attribute}.Title`}
          optional
          filterErrors={Name.requiredErrorsOnly}
          scrollIntoView={this.props.scrollIntoView}
        >
          {i18n.m('relationships.relatives.para.alias')}
          <Name
            name="Name"
            className="alias-name"
            attribute={`${attribute}.Name`}
            aria-label="Provide other names that your relative used"
            {...this.props.Name}
            onUpdate={this.updateName}
            onError={this.handleNameError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
          />
        </Field>

        <Show when={this.props.hideMaiden === false}>
          <Branch
            name="MaidenName"
            attribute={`${attribute}.MaidenName`}
            aria-label="Provide your mother's maiden name"
            label={i18n.t('relationships.relatives.heading.alias.maiden')}
            labelSize="h4"
            className="alias-maiden"
            {...this.props.MaidenName}
            onUpdate={this.updateMaidenName}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            onError={this.props.onError}
          />
        </Show>

        <Field
          attribute={`${attribute}.Dates`}
          optional
          scrollIntoView={this.props.scrollIntoView}
        >
          <DateRange
            name="Dates"
            attribute={`${attribute}.Dates`}
            className="alias-dates"
            {...this.props.Dates}
            prefix="relative"
            minDateEqualTo
            minDate={this.props.minDate}
            relationship={this.props.relationship}
            onUpdate={this.updateDates}
            onError={this.props.onError}
            required={this.props.required}
            hideFromDay
            hideToDay
          />
        </Field>

        <Field
          title={i18n.t('relationships.relatives.heading.alias.reason')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Reason`}
          titleSize="h4"
        >
          <Textarea
            name="Reason"
            attribute={`${attribute}.Reason`}
            className="alias-reason"
            {...this.props.Reason}
            onUpdate={this.updateReason}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>
        <Comments
          title={i18n.t('relationships.comments.optional')}
          name={`${attribute}Comments`}
          className="optional-comment"
          attribute={`${attribute}Comments`}
          {...this.props.Comments}
          onUpdate={this.updateComments}
          required={false}
        />
      </div>
    );
  };
}

Alias.defaultProps = {
  Name: {},
  MaidenName: {},
  Dates: {},
  Reason: {},
  Comments: {},
  hideMaiden: false,
  NameOptions: {}, // pvq only
  NameOptionsExplanation: {}, // pvq only
  onUpdate: (queue) => { },
  onError: (value, arr) => arr,
};
